import type { TFunction } from 'i18next';
import {
  strandedRuleIdsByStep,
  type ProcessDraftAssignment,
  type ProcessDraftStep,
  type PublishValidationError
} from '@bliks-io/contracts';
import type { StepFieldKey } from '@bliks-io/ui';
import type { DraftEditorEvent } from '../draftEvents';
import { canMarkAsEnd } from '../canMarkAsEnd';
import { expandRuleIdToOutputGroup } from '../graph/expandRuleIdToOutputGroup';
import { getFieldLabel } from './publishErrorFormatting';

export type DraftIssueKind =
  | 'actionType'
  | 'taskDescription'
  | 'output'
  | 'assignee'
  | 'rules'
  | 'ruleOutput'
  | 'ruleCondition'
  | 'assignmentInput'
  | 'eventName'
  | 'eventDescription'
  | 'noInputs'
  | 'noOutputs'
  | 'decisionNoOutputs'
  | 'orphan'
  | 'ruleNoOutput'
  | 'eventDisconnected'
  | 'unreachable'
  | 'noStartEvent'
  | 'unknown';

export type DraftIssueNodeKind = 'step' | 'decision' | 'assignment' | 'event' | 'process';
export type DraftIssueCategory = 'missing' | 'connection';
/** A step-panel field, or an event-panel field. */
export type DraftIssueField = StepFieldKey | 'name' | 'description';

export type DraftIssueQuickFix =
  | { kind: 'markStepEnd'; stepId: string }
  | { kind: 'markRulesEnd'; stepId: string; ruleIds: readonly string[] };

export interface DraftIssue {
  key: string;
  /** null = process-level: there is no node to go to. */
  nodeId: string | null;
  kind: DraftIssueKind;
  category: DraftIssueCategory;
  /** undefined = the panel's issue summary only. */
  field?: DraftIssueField;
  /** Decision rule rows the issue flags, inside the `rules` field. */
  ruleIndexes?: readonly number[];
  title: string;
  hint?: string;
  quickFix?: DraftIssueQuickFix;
}

export interface DraftIssueGroup {
  nodeId: string | null;
  nodeKind: DraftIssueNodeKind;
  nodeLabel: string;
  stepNumber?: number;
  issues: DraftIssue[];
}

interface IssueText {
  title: string;
  hint?: string;
}

interface IssueValues {
  number?: number;
  output?: string;
  /** The title of an `unknown` issue, already labelled. */
  message?: string;
}

type GraphError = PublishValidationError['graphErrors'][number];
type ContentIssue = PublishValidationError['contentErrors'][number]['issues'][number];

interface IssueKindSpec {
  category: DraftIssueCategory;
  text: (t: TFunction, values: IssueValues) => IssueText;
}

const missing = (text: IssueKindSpec['text']): IssueKindSpec => ({ category: 'missing', text });
const connection = (text: IssueKindSpec['text']): IssueKindSpec => ({
  category: 'connection',
  text
});

// One static `t` key per kind: the translation checker only sees literal keys.
const ISSUE_KINDS: Record<DraftIssueKind, IssueKindSpec> = {
  actionType: missing((t) => ({ title: t('processDraft.issues.kinds.actionType.title') })),
  taskDescription: missing((t) => ({
    title: t('processDraft.issues.kinds.taskDescription.title')
  })),
  output: missing((t) => ({ title: t('processDraft.issues.kinds.output.title') })),
  assignee: missing((t) => ({ title: t('processDraft.issues.kinds.assignee.title') })),
  rules: missing((t) => ({ title: t('processDraft.issues.kinds.rules.title') })),
  ruleOutput: missing((t, { number }) => ({
    title: t('processDraft.issues.kinds.ruleOutput.title', { number })
  })),
  ruleCondition: missing((t, { number }) => ({
    title: t('processDraft.issues.kinds.ruleCondition.title', { number })
  })),
  assignmentInput: connection((t) => ({
    title: t('processDraft.issues.kinds.assignmentInput.title'),
    hint: t('processDraft.issues.kinds.assignmentInput.hint')
  })),
  eventName: missing((t) => ({ title: t('processDraft.issues.kinds.eventName.title') })),
  eventDescription: missing((t) => ({
    title: t('processDraft.issues.kinds.eventDescription.title')
  })),
  noInputs: connection((t) => ({
    title: t('processDraft.issues.kinds.noInputs.title'),
    hint: t('processDraft.issues.kinds.noInputs.hint')
  })),
  noOutputs: connection((t) => ({
    title: t('processDraft.issues.kinds.noOutputs.title'),
    hint: t('processDraft.issues.kinds.noOutputs.hint')
  })),
  decisionNoOutputs: connection((t) => ({
    title: t('processDraft.issues.kinds.decisionNoOutputs.title'),
    hint: t('processDraft.issues.kinds.decisionNoOutputs.hint')
  })),
  orphan: connection((t) => ({
    title: t('processDraft.issues.kinds.orphan.title'),
    hint: t('processDraft.issues.kinds.orphan.hint')
  })),
  ruleNoOutput: connection((t, { output }) => ({
    title: output
      ? t('processDraft.issues.kinds.ruleNoOutput.title', { output })
      : t('processDraft.issues.kinds.ruleNoOutput.titleUnnamed'),
    hint: t('processDraft.issues.kinds.ruleNoOutput.hint')
  })),
  eventDisconnected: connection((t) => ({
    title: t('processDraft.issues.kinds.eventDisconnected.title'),
    hint: t('processDraft.issues.kinds.eventDisconnected.hint')
  })),
  unreachable: connection((t) => ({
    title: t('processDraft.issues.kinds.unreachable.title'),
    hint: t('processDraft.issues.kinds.unreachable.hint')
  })),
  noStartEvent: connection((t) => ({
    title: t('processDraft.issues.kinds.noStartEvent.title'),
    hint: t('processDraft.issues.kinds.noStartEvent.hint')
  })),
  unknown: missing((_t, { message }) => ({ title: message ?? '' }))
};

/** Where an issue sits before its node and copy are attached. */
interface IssueSpec {
  kind: DraftIssueKind;
  field?: DraftIssueField;
  ruleIndexes?: readonly number[];
  values?: IssueValues;
  quickFix?: DraftIssueQuickFix;
  /** Tells apart several issues of one kind on one node: an identity, not a position. */
  discriminator?: string;
}

const CONTENT_PATH_SPECS: Readonly<Record<string, IssueSpec>> = {
  actionType: { kind: 'actionType', field: 'actionType' },
  'task.taskDescription': { kind: 'taskDescription', field: 'task.taskDescription' },
  'task.output': { kind: 'output', field: 'task.output' },
  assignedTo: { kind: 'assignee', field: 'assignedTo' },
  rules: { kind: 'rules', field: 'rules' },
  inputs: { kind: 'assignmentInput', field: 'inputs' },
  name: { kind: 'eventName', field: 'name' },
  description: { kind: 'eventDescription', field: 'description' }
};

function contentIssueSpec(
  { path, message }: ContentIssue,
  step: ProcessDraftStep | undefined,
  t: TFunction
): IssueSpec {
  const [head, index, leaf] = path;
  if (head === 'rules' && typeof index === 'number' && (leaf === 'output' || leaf === 'inputs')) {
    const ruleId = step?.rules?.[index]?._id;
    return {
      kind: leaf === 'output' ? 'ruleOutput' : 'ruleCondition',
      field: 'rules',
      ruleIndexes: [index],
      values: { number: index + 1 },
      discriminator: ruleId ? String(ruleId) : String(index)
    };
  }
  const pathKey = path.join('.');
  const spec = CONTENT_PATH_SPECS[pathKey];
  if (spec) return spec;
  // A path the validator gained after this mapping still blocks Publish, so it
  // stays listed (labelled like the publish-error dialog does) to keep the count true.
  const label = getFieldLabel(t, pathKey);
  return {
    kind: 'unknown',
    values: { message: label === pathKey ? message : `${label}: ${message}` },
    discriminator: pathKey
  };
}

interface NodeInfo {
  kind: DraftIssueNodeKind;
  label: string;
  stepNumber?: number;
  step?: ProcessDraftStep;
  /** Flow order: process, numbered steps, unnumbered steps, assignments, events. */
  order: readonly [tier: number, rank: number];
}

type StrandedRuleIdsByStep = ReadonlyMap<string, ReadonlySet<string>>;

/**
 * Marking a rule group as the end cascades over every rule of the group, so it
 * is offered only while none of them is connected or terminal: an output
 * rename still saving can put a connected rule into a stranded group.
 */
export function isOutputGroupStranded(
  strandedRuleIds: StrandedRuleIdsByStep,
  stepId: string,
  ruleIds: readonly string[]
): boolean {
  const stranded = strandedRuleIds.get(stepId);
  return ruleIds.length > 0 && ruleIds.every((ruleId) => stranded?.has(ruleId) === true);
}

interface GraphContext {
  steps: readonly ProcessDraftStep[];
  strandedRuleIds: StrandedRuleIdsByStep;
}

function ruleNoOutputSpec(
  nodeId: string | null,
  node: NodeInfo,
  ruleId: string | undefined,
  strandedRuleIds: StrandedRuleIdsByStep
): IssueSpec {
  const rules = node.step?.rules ?? [];
  const ruleIds = ruleId ? expandRuleIdToOutputGroup(rules, ruleId) : [];
  const ruleIndexes = rules.flatMap((rule, index) =>
    ruleIds.includes(String(rule._id)) ? [index] : []
  );
  // The group is its shared output; neither rule order nor a deleted sibling changes it.
  const output = rules[ruleIndexes[0]]?.output?.trim() ?? '';
  return {
    kind: 'ruleNoOutput',
    field: 'rules',
    ruleIndexes,
    values: { output },
    quickFix:
      nodeId !== null && isOutputGroupStranded(strandedRuleIds, nodeId, ruleIds)
        ? { kind: 'markRulesEnd', stepId: nodeId, ruleIds }
        : undefined,
    discriminator: output
  };
}

function noOutputsSpec(
  nodeId: string | null,
  node: NodeInfo,
  steps: readonly ProcessDraftStep[]
): IssueSpec {
  if (node.kind === 'decision') return { kind: 'decisionNoOutputs', field: 'rules' };
  return {
    kind: 'noOutputs',
    // A step without a type has no successors section in the panel.
    field: node.step?.actionType ? 'successors' : undefined,
    quickFix:
      nodeId !== null && canMarkAsEnd(nodeId, steps)
        ? { kind: 'markStepEnd', stepId: nodeId }
        : undefined
  };
}

function graphIssueSpec(
  error: GraphError,
  nodeId: string | null,
  node: NodeInfo,
  { steps, strandedRuleIds }: GraphContext
): IssueSpec {
  const reason: string = error.reason;
  switch (error.reason) {
    case 'no-start-event':
      return { kind: 'noStartEvent' };
    case 'no-inputs':
      return { kind: 'noInputs', field: 'inputs' };
    case 'no-outputs':
      return noOutputsSpec(nodeId, node, steps);
    case 'orphan':
      return { kind: 'orphan', field: 'inputs' };
    case 'rule-no-output':
      return ruleNoOutputSpec(nodeId, node, error.ruleId, strandedRuleIds);
    case 'event-disconnected':
      return { kind: 'eventDisconnected' };
    case 'unreachable':
      return { kind: 'unreachable', field: node.kind === 'event' ? undefined : 'inputs' };
    default:
      // A reason a newer backend rejects with: listed by its name, to keep the list true.
      return { kind: 'unknown', values: { message: reason }, discriminator: reason };
  }
}

function buildNodeInfo(
  args: Omit<BuildDraftIssuesArgs, 'validation'>
): Map<string | null, NodeInfo> {
  const { steps, draftAssignments, draftEvents, stepNumberByStableId, t } = args;
  const unnamedStep = t('processDraft.publishErrors.unnamedStep');
  const unnamedEvent = t('processDraft.publishErrors.unnamedEvent');
  const newDecision = t('processFlowEditor.newDecision');
  const newTask = t('processFlowEditor.newTask');
  const nodes = new Map<string | null, NodeInfo>([
    [null, { kind: 'process', label: t('processDraft.publishErrors.processLevel'), order: [0, 0] }]
  ]);
  steps.forEach((step, index) => {
    const id = String(step.stableStepId);
    const stepNumber = stepNumberByStableId.get(id);
    const name = step.generatedName?.trim();
    // A decision's `task` bucket is leftover text kept across a type toggle, never its name.
    const isDecision = step.actionType === 'decision';
    nodes.set(id, {
      kind: isDecision ? 'decision' : 'step',
      label: isDecision
        ? name || newDecision
        : name || step.task?.taskDescription?.trim() || newTask,
      stepNumber,
      step,
      order: stepNumber === undefined ? [2, index] : [1, stepNumber]
    });
  });
  draftAssignments.forEach((assignment, index) => {
    nodes.set(String(assignment.stableStepId), {
      kind: 'assignment',
      label: assignment.generatedName?.trim() || unnamedStep,
      order: [3, index]
    });
  });
  draftEvents.forEach((event, index) => {
    nodes.set(event._id, {
      kind: 'event',
      label: event.name.trim() || unnamedEvent,
      order: [4, index]
    });
  });
  return nodes;
}

export interface BuildDraftIssuesArgs {
  validation: PublishValidationError;
  steps: readonly ProcessDraftStep[];
  draftAssignments: readonly ProcessDraftAssignment[];
  draftEvents: readonly DraftEditorEvent[];
  stepNumberByStableId: ReadonlyMap<string, number>;
  t: TFunction;
}

/**
 * A publish validation as the editor's issue list: one group per node, in flow
 * order. Issues of a node missing from the arrays are left out: a server
 * rejection can name a node a co-editor has deleted since.
 */
export function buildDraftIssues(args: BuildDraftIssuesArgs): DraftIssueGroup[] {
  const { validation, steps, draftAssignments, t } = args;
  const nodes = buildNodeInfo(args);
  const graphContext: GraphContext = {
    steps,
    strandedRuleIds: strandedRuleIdsByStep(steps, draftAssignments)
  };
  const groups = new Map<string | null, { group: DraftIssueGroup; order: NodeInfo['order'] }>();
  const issueKey = (nodeId: string | null, spec: IssueSpec) =>
    [nodeId ?? 'process', spec.kind, spec.discriminator ?? ''].join(':');

  const push = (nodeId: string | null, node: NodeInfo, spec: IssueSpec) => {
    const { category, text } = ISSUE_KINDS[spec.kind];
    const { title, hint } = text(t, spec.values ?? {});
    let entry = groups.get(nodeId);
    if (!entry) {
      const { kind, label, stepNumber, order } = node;
      entry = {
        group: { nodeId, nodeKind: kind, nodeLabel: label, stepNumber, issues: [] },
        order
      };
      groups.set(nodeId, entry);
    }
    entry.group.issues.push({
      key: issueKey(nodeId, spec),
      nodeId,
      kind: spec.kind,
      category,
      field: spec.field,
      ruleIndexes: spec.ruleIndexes,
      title,
      hint,
      quickFix: spec.quickFix
    });
  };

  for (const { stepId, issues } of validation.contentErrors) {
    const node = nodes.get(stepId);
    if (!node) continue;
    for (const issue of issues) {
      const spec = contentIssueSpec(issue, node.step, t);
      // An assignment without inputs fails both the content and the graph rule
      // for the same cause; the graph row (`noInputs`) stands for both.
      if (
        spec.kind === 'assignmentInput' &&
        validation.graphErrors.some(
          (error) => error.reason === 'no-inputs' && error.stepId === stepId
        )
      ) {
        continue;
      }
      push(stepId, node, spec);
    }
  }
  // One issue per stranded output group: its rules share one canvas handle.
  const shownStrandedGroups = new Set<string>();
  for (const error of validation.graphErrors) {
    const nodeId = error.stepId ?? null;
    const node = nodes.get(nodeId);
    if (!node) continue;
    const spec = graphIssueSpec(error, nodeId, node, graphContext);
    if (spec.kind === 'ruleNoOutput') {
      const strandedGroup = issueKey(nodeId, spec);
      if (shownStrandedGroups.has(strandedGroup)) continue;
      shownStrandedGroups.add(strandedGroup);
    }
    push(nodeId, node, spec);
  }

  return [...groups.values()]
    .sort((a, b) => a.order[0] - b.order[0] || a.order[1] - b.order[1])
    .map(({ group }) => group);
}
